"use client"

import { useEffect, useRef, useState } from "react"
import axios from "axios"
import "ol/ol.css"
import { Map, View } from "ol"
import TileLayer from "ol/layer/Tile"
import OSM from "ol/source/OSM"
import { fromLonLat } from "ol/proj"
import { Vector as VectorLayer } from "ol/layer"
import { Vector as VectorSource } from "ol/source"
import { Feature } from "ol"
import { Point } from "ol/geom"
import { Style, Icon } from "ol/style"
import Overlay from "ol/Overlay"
import { boundingExtent } from 'ol/extent'
import serviceMapIcon from './serviceMap3.png';
import { format } from "date-fns"

// Replace Map with plain object
const locationCache = Object.create(null);

// Clear cache after 24 hours
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const clearExpiredCache = () => {
  const now = Date.now();
  Object.keys(locationCache).forEach(key => {
    if (now - locationCache[key].timestamp > CACHE_DURATION) {
      delete locationCache[key];
    }
  });
};

// Add this utility function at the top level
const queue = [];
let isProcessing = false;

const processQueue = async () => {
  if (isProcessing || queue.length === 0) return;
  isProcessing = true;

  while (queue.length > 0) {
    const { address, resolve, reject } = queue.shift();
    try {
      await new Promise(r => setTimeout(r, 1500)); // 1.5 second delay between requests
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          q: address,
          format: 'json',
          limit: 1,
        }
        // Removed User-Agent header as it's not allowed in browsers
      });

      if (response.data.length > 0) {
        const result = response.data[0];
        resolve([parseFloat(result.lon), parseFloat(result.lat)]);
      } else {
        resolve(null);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      resolve(null);
    }
  }

  isProcessing = false;
};

const OpenLayersServiceLocations = ({ fetchedData, selectedDate, selectedCity }) => {
  const mapRef = useRef()
  const mapInstance = useRef(null)
  const vectorLayerRef = useRef(null)
  const userLocationLayerRef = useRef(null)
  const [selectedPlace, setSelectedPlace] = useState(null)
  const [isLoading, setIsLoading] = useState(false);
  const allMarkersRef = useRef([]);
  const overlayRef = useRef(null);

  const fitMapToMarkers = () => {
    if (!mapInstance.current || allMarkersRef.current.length === 0) return;

    const coordinates = allMarkersRef.current.map(marker => 
      marker.getGeometry().getCoordinates()
    );
    
    const extent = boundingExtent(coordinates);
    mapInstance.current.getView().fit(extent, {
      padding: [50, 50, 50, 50],
      maxZoom: 15
    });
  };

  // Add this function before the useEffects
  const updateMarkers = async (data) => {
    if (!mapInstance.current) return;
    setIsLoading(true);

    try {
      if (vectorLayerRef.current) {
        mapInstance.current.removeLayer(vectorLayerRef.current);
        vectorLayerRef.current = null;
      }

      if (data) {
        const vectorLayer = await geocodeAddressesAndAddMarkers(data);
        if (vectorLayer) {
          vectorLayerRef.current = vectorLayer;
          mapInstance.current.addLayer(vectorLayer);
          fitMapToMarkers();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getEarliestSlot = (slots) => {
    if (!slots || slots.length === 0) return "Selectează o dată";
    const sortedSlots = slots.sort((a, b) => new Date(a.slotStart) - new Date(b.slotStart));
    return format(new Date(sortedSlots[0].slotStart), "HH:mm");
  };

  // Initialize map only once with selected city as default center
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    // Default coordinates for Romania if selected city is not available
    let defaultCenter = [23.594, 46.7712]; // Default to Cluj-Napoca
    
    if (fetchedData && fetchedData.length > 0 && fetchedData[0].longitude && fetchedData[0].latitude) {
      // Use the first shop's coordinates to center the map if available
      defaultCenter = [fetchedData[0].longitude, fetchedData[0].latitude];
    }

    mapInstance.current = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat(defaultCenter),
        zoom: 13,
      }),
    });

    // Create overlay once and store reference
    overlayRef.current = new Overlay({
      element: document.createElement("div"),
      positioning: "bottom-center",
      stopEvent: false,
      id: "popup",
    });
    mapInstance.current.addOverlay(overlayRef.current);

    // Add click handlers
    mapInstance.current.on("click", handleMapClick);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      if (mapInstance.current) {
        mapInstance.current.setTarget(null);
        mapInstance.current = null;
      }
      clearExpiredCache(); // Clear expired cache entries on unmount
    };
  }, []);

  // Handle data updates
  useEffect(() => {
    if (!mapInstance.current || !fetchedData) return;
    
    const updateMap = async () => {
      try {
        await updateMarkers(fetchedData);
      } catch (error) {
        console.error('Error updating markers:', error);
      }
    };

    updateMap();
  }, [fetchedData, selectedDate]);

  // Modify addUserLocationMarker to ensure it stays on top
  const addUserLocationMarker = (coordinates) => {
    if (!mapInstance.current) return;

    if (userLocationLayerRef.current) {
      mapInstance.current.removeLayer(userLocationLayerRef.current);
    }

    const vectorSource = new VectorSource();
    const feature = new Feature({
      geometry: new Point(fromLonLat(coordinates)),
      name: "Locația ta",
      isUserLocation: true, // Add flag to identify user location feature
    });

    feature.setStyle(
      new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
          scale: 0.1, // Slightly larger
          zIndex: 2000, // Even higher zIndex
        }),
      }),
    );

    vectorSource.addFeature(feature);
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 2000, // Ensure it's above other layers
    });

    userLocationLayerRef.current = vectorLayer;
    mapInstance.current.addLayer(vectorLayer);

    // Center on user location with animation
    mapInstance.current.getView().animate({
      center: fromLonLat(coordinates),
      zoom: 14,
      duration: 1000
    });
  };

  // Separate useEffect for user location
  useEffect(() => {
    if (!mapInstance.current) return;

    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userCoordinates = [position.coords.longitude, position.coords.latitude];
            addUserLocationMarker(userCoordinates);
          },
          (error) => {
            console.warn("Geolocation error:", error.message);
            // Fallback to Cluj-Napoca
            addUserLocationMarker([23.594, 46.7712]);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      }
    };

    getUserLocation();
  }, [mapInstance.current]); // Only run when map is initialized

  // Modify the geocodeAddressesAndAddMarkers function to use coordinates when available
  const geocodeAddressesAndAddMarkers = async (data) => {
    if (!mapInstance.current) return null;

    try {
      const vectorSource = new VectorSource();
      const items = Array.isArray(data) ? data : Object.values(data).flat();
      const validItems = items.filter(item => item && (item.address || (item.longitude && item.latitude)));
      
      allMarkersRef.current = []; // Reset markers array

      for (let i = 0; i < validItems.length; i += 3) {
        const batch = validItems.slice(i, i + 3);
        const features = await Promise.all(
          batch.map(async (item) => {
            let coordinates;
            
            // If longitude and latitude are available, use them directly
            if (item.longitude && item.latitude) {
              coordinates = [parseFloat(item.longitude), parseFloat(item.latitude)];
            } else if (item.address) {
              // Fall back to geocoding if no coordinates
              coordinates = await geocodeAddress(item.address);
            }
            
            if (!coordinates) return null;

            const earliestSlot = getEarliestSlot(item.availableSlots);

            return new Feature({
              geometry: new Point(fromLonLat(coordinates)),
              name: item.name || item.brandName,
              address: item.address,
              time: earliestSlot,
            });
          })
        );

        const validFeatures = features.filter(Boolean);
        validFeatures.forEach(feature => {
          feature.setStyle(
            new Style({
              image: new Icon({
                anchor: [0.5, 1],
                src: serviceMapIcon,
                scale: 0.15,
              }),
            })
          );
          allMarkersRef.current.push(feature);
        });

        vectorSource.addFeatures(validFeatures);
      }

      const vectorLayer = new VectorLayer({ source: vectorSource });
      return vectorLayer;
    } catch (error) {
      console.error('Error creating markers:', error);
      return null;
    }
  };

  // Replace the geocodeAddress function with this version
  const geocodeAddress = async (address) => {
    clearExpiredCache();

    // Check cache first
    if (address in locationCache) {
      return locationCache[address].coordinates;
    }

    return new Promise((resolve, reject) => {
      queue.push({
        address,
        resolve: (coordinates) => {
          if (coordinates) {
            locationCache[address] = {
              coordinates,
              timestamp: Date.now()
            };
          }
          resolve(coordinates);
        },
        reject
      });
      processQueue();
    });
  };

  const handleClickOutside = (event) => {
    // Check if click is outside the map
    if (!mapRef.current.contains(event.target)) {
      if (overlayRef.current) {
        overlayRef.current.setPosition(undefined);
      }
    }
  };

  const handleMapClick = (event) => {
    const feature = mapInstance.current.forEachFeatureAtPixel(event.pixel, (feature) => feature)

    if (feature) {
      const coordinates = feature.getGeometry().getCoordinates()
      const properties = feature.getProperties()

      setSelectedPlace({
        position: coordinates,
        name: properties.name,
        address: properties.address,
        time: properties.time,
      })

      const popupContent = `
        <div class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg">
          <h3 class="font-bold text-lg mb-1 text-slate-900 dark:text-white">${properties.name}</h3>
          <p class="text-sm mb-1 text-slate-700 dark:text-slate-300">${properties.address}</p>
          ${properties.time ? `<p class="text-sm text-slate-600 dark:text-slate-400">Disponibil de la: ${properties.time}</p>` : ""}
        </div>
      `

      overlayRef.current.getElement().innerHTML = popupContent
      overlayRef.current.setPosition(coordinates)
    } else {
      overlayRef.current.setPosition(undefined)
    }
  }

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full rounded-lg overflow-hidden" style={{ minHeight: "250px" }} />
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg">
            <p className="text-slate-900 dark:text-white">Așezăm ateliere pe hartă...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenLayersServiceLocations


